import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { verifyCheckout } from "@/api/subscriptions";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const plan = searchParams.get("plan");
  const sessionId = searchParams.get("session_id");
  const [activating, setActivating] = useState(true);
  const [activationError, setActivationError] = useState(false);

  useEffect(() => {
    async function activate() {
      if (sessionId && plan) {
        try {
          await verifyCheckout(sessionId, plan);
        } catch (err) {
          console.error("Subscription activation failed:", err);
          setActivationError(true);
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["access-status"] });
      setActivating(false);
    }
    activate();
  }, [sessionId, plan, queryClient]);

  const planLabels: Record<string, string> = {
    one_week: "1 Week Access",
    two_weeks: "2 Weeks Access",
    three_months: "3 Months Access",
    monthly: "Monthly Subscription",
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-xl border border-gray-200 bg-white p-10 shadow-sm text-center">
          {/* Icon */}
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-100">
            <svg
              className="h-10 w-10 text-success-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful</h1>

          {plan && planLabels[plan] ? (
            <p className="text-gray-600 mb-1">
              You now have <span className="font-semibold text-gray-900">{planLabels[plan]}</span>.
            </p>
          ) : null}

          <p className="text-sm text-gray-500 mb-8">
            Full access to all mock exams, categories, and practice materials is now active.
          </p>

          {/* Access features */}
          <ul className="mb-8 space-y-2 text-left text-sm text-gray-600">
            <li className="flex items-center gap-3">
              <svg className="h-5 w-5 text-success-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All mock exams unlocked
            </li>
            <li className="flex items-center gap-3">
              <svg className="h-5 w-5 text-success-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Category practice sessions
            </li>
            <li className="flex items-center gap-3">
              <svg className="h-5 w-5 text-success-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Traffic signs quiz
            </li>
          </ul>

          {activationError && (
            <div className="mb-4 rounded-lg bg-danger-50 border border-danger-200 px-4 py-3 text-sm text-danger-700">
              There was a problem activating your subscription. Please contact support with your payment receipt.
            </div>
          )}

          <div className="flex flex-col gap-3">
            {activating ? (
              <div className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white opacity-70">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Activating your access...
              </div>
            ) : (
              <Link
                to="/exams"
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
              >
                Start Practising
              </Link>
            )}
            <Link
              to="/profile"
              className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
            >
              View my subscription
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
